import React, { useState, useEffect } from 'react';
import {
    MapPin, Calendar, ChevronDown, Info, FileText, Calculator,
    Loader2, Navigation, Crosshair, AlertCircle
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import LoginModal from './LoginModal';
import PreofertaModal from './PreofertaModal';

// Subcomponents
import {
    LoteHeader,
    LoteVideo,
    LoteDataSidebar,
    LotePromedios,
    LoteTabGeneral,
    LoteTabObservaciones
} from './LoteDetalle';

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Define red icon for destination
const redIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Component to handle map clicks
const MapClickHandler = ({ onMapClick }) => {
    useMapEvents({
        click: (e) => {
            onMapClick(e.latlng);
        }
    });
    return null;
};

/**
 * LoteDetalleContent - Reusable component for displaying lote details
 * Refactored to use subcomponents for better maintainability
 */
const LoteDetalleContent = ({
    lote,
    onShare,
    showHeader = true,
    showButtons = true
}) => {
    const [activeTab, setActiveTab] = useState('general');
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showPreofertaModal, setShowPreofertaModal] = useState(false);

    // Mobile accordion state
    const [expandedSections, setExpandedSections] = useState({
        general: true,
        ubicacion: false,
        observaciones: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Freight calculator state
    const [direccionFlete, setDireccionFlete] = useState('');
    const [precioPorKm, setPrecioPorKm] = useState('');
    const [destinoCoords, setDestinoCoords] = useState(null);
    const [routeCoords, setRouteCoords] = useState([]);
    const [distanciaKm, setDistanciaKm] = useState(null);
    const [costoFlete, setCostoFlete] = useState(null);
    const [calculandoFlete, setCalculandoFlete] = useState(false);
    const [fleteError, setFleteError] = useState('');

    // Autocomplete state
    const [sugerencias, setSugerencias] = useState([]);
    const [buscandoSugerencias, setBuscandoSugerencias] = useState(false);
    const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
    const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);

    // Search for address suggestions
    const buscarSugerencias = async (query) => {
        if (query.length < 3) {
            setSugerencias([]);
            return;
        }
        setBuscandoSugerencias(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}, Uruguay&limit=5&addressdetails=1`,
                { headers: { 'Accept-Language': 'es' } }
            );
            const data = await response.json();
            setSugerencias(data || []);
            setMostrarSugerencias(true);
        } catch (error) {
            console.error('Error buscando sugerencias:', error);
            setSugerencias([]);
        } finally {
            setBuscandoSugerencias(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (direccionFlete.trim() && !destinoCoords) {
                buscarSugerencias(direccionFlete);
            } else {
                setSugerencias([]);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [direccionFlete, destinoCoords]);

    const handleSeleccionarSugerencia = (sugerencia) => {
        setDireccionFlete(sugerencia.display_name);
        setDestinoCoords({
            lat: parseFloat(sugerencia.lat),
            lng: parseFloat(sugerencia.lon)
        });
        setMostrarSugerencias(false);
        setSugerencias([]);
        setRouteCoords([]);
        setDistanciaKm(null);
        setCostoFlete(null);
    };

    const getRoute = async (origin, destination) => {
        try {
            const response = await fetch(
                `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`
            );
            const data = await response.json();
            if (data.routes && data.routes.length > 0) {
                const route = data.routes[0];
                return {
                    distance: route.distance / 1000,
                    coordinates: route.geometry.coordinates.map(coord => [coord[1], coord[0]])
                };
            }
            return null;
        } catch (error) {
            console.error('Error getting route:', error);
            return null;
        }
    };

    const handleMapClick = async (latlng) => {
        setDestinoCoords(latlng);
        setDireccionFlete(`Cargando ubicación...`);
        setMostrarSugerencias(false);
        setRouteCoords([]);
        setDistanciaKm(null);
        setCostoFlete(null);

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&zoom=18&addressdetails=1`,
                { headers: { 'Accept-Language': 'es' } }
            );
            const data = await response.json();
            if (data && data.address) {
                const addr = data.address;
                const parts = [];
                if (addr.road) parts.push(addr.road);
                if (addr.hamlet) parts.push(addr.hamlet);
                if (addr.village) parts.push(addr.village);
                if (addr.town) parts.push(addr.town);
                if (addr.suburb) parts.push(addr.suburb);
                if (addr.city) parts.push(addr.city);
                if (addr.municipality) parts.push(addr.municipality);
                if (addr.state) parts.push(addr.state);

                if (parts.length <= 1) {
                    setDireccionFlete(`${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)} (${parts[0] || 'Uruguay'})`);
                } else {
                    const uniqueParts = [...new Set(parts)];
                    setDireccionFlete(uniqueParts.slice(0, 4).join(', '));
                }
            } else {
                setDireccionFlete(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
            }
        } catch (error) {
            console.error('Error reverse geocoding:', error);
            setDireccionFlete(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
        }
    };

    const usarMiUbicacion = () => {
        if (!navigator.geolocation) {
            setFleteError('Tu navegador no soporta geolocalización');
            return;
        }
        setObteniendoUbicacion(true);
        setFleteError('');

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const latlng = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                setDestinoCoords(latlng);
                setDireccionFlete('Obteniendo ubicación...');
                setRouteCoords([]);
                setDistanciaKm(null);
                setCostoFlete(null);

                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&zoom=18&addressdetails=1`,
                        { headers: { 'Accept-Language': 'es' } }
                    );
                    const data = await response.json();
                    if (data && data.address) {
                        const addr = data.address;
                        const parts = [];
                        if (addr.road) parts.push(addr.road);
                        if (addr.hamlet) parts.push(addr.hamlet);
                        if (addr.village) parts.push(addr.village);
                        if (addr.town) parts.push(addr.town);
                        if (addr.suburb) parts.push(addr.suburb);
                        if (addr.city) parts.push(addr.city);
                        if (addr.municipality) parts.push(addr.municipality);
                        if (addr.state) parts.push(addr.state);

                        if (parts.length <= 1) {
                            setDireccionFlete(`${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)} (${parts[0] || 'Uruguay'})`);
                        } else {
                            const uniqueParts = [...new Set(parts)];
                            setDireccionFlete(uniqueParts.slice(0, 4).join(', '));
                        }
                    } else {
                        setDireccionFlete(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
                    }
                } catch (error) {
                    console.error('Error reverse geocoding:', error);
                    setDireccionFlete(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
                }
                setObteniendoUbicacion(false);
            },
            (error) => {
                console.error('Geolocation error:', error);
                setObteniendoUbicacion(false);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        setFleteError('Permiso de ubicación denegado');
                        break;
                    case error.POSITION_UNAVAILABLE:
                        setFleteError('Ubicación no disponible');
                        break;
                    case error.TIMEOUT:
                        setFleteError('Tiempo de espera agotado');
                        break;
                    default:
                        setFleteError('Error al obtener ubicación');
                }
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    const getCoordinatesFromAddress = async (address) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
                { headers: { 'Accept-Language': 'es' } }
            );
            const data = await response.json();
            if (data && data.length > 0) {
                return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
            }
            return null;
        } catch (error) {
            console.error('Error geocoding address:', error);
            return null;
        }
    };

    const calcularFlete = async () => {
        if (!direccionFlete.trim() || !precioPorKm) {
            setFleteError('Complete la dirección y el precio por km');
            return;
        }
        setCalculandoFlete(true);
        setFleteError('');
        setRouteCoords([]);
        setDistanciaKm(null);
        setCostoFlete(null);

        try {
            let destino = destinoCoords;
            if (!destino) {
                destino = await getCoordinatesFromAddress(direccionFlete);
                if (!destino) {
                    setFleteError('No se pudo encontrar la dirección de destino');
                    setCalculandoFlete(false);
                    return;
                }
                setDestinoCoords(destino);
            }
            const origen = { lat: lote.latitude, lng: lote.longitude };
            const routeData = await getRoute(origen, destino);
            if (routeData) {
                setRouteCoords(routeData.coordinates);
                setDistanciaKm(routeData.distance);
                setCostoFlete(routeData.distance * parseFloat(precioPorKm));
            } else {
                setFleteError('No se pudo calcular la ruta');
            }
        } catch (error) {
            setFleteError('Error al calcular el flete');
            console.error('Error:', error);
        } finally {
            setCalculandoFlete(false);
        }
    };

    const handleShare = () => {
        if (onShare) {
            onShare();
        } else if (navigator.share) {
            navigator.share({
                title: `Lote #${lote.numero_lote} - ${lote.categoria}`,
                text: `${lote.cabezas} cabezas - ${lote.departamento}`,
                url: window.location.href,
            }).catch(console.error);
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Enlace copiado al portapapeles');
        }
    };

    const handlePreofertar = () => {
        const user = localStorage.getItem('plaza_user');
        if (user) {
            setShowPreofertaModal(true);
        } else {
            setShowLoginModal(true);
        }
    };

    if (!lote) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">No hay datos del lote</p>
            </div>
        );
    }

    // Freight calculator props for Ubicación tab
    const fleteProps = {
        direccionFlete,
        setDireccionFlete,
        precioPorKm,
        setPrecioPorKm,
        destinoCoords,
        setDestinoCoords,
        routeCoords,
        setRouteCoords,
        distanciaKm,
        setDistanciaKm,
        costoFlete,
        setCostoFlete,
        calculandoFlete,
        fleteError,
        sugerencias,
        buscandoSugerencias,
        mostrarSugerencias,
        setMostrarSugerencias,
        obteniendoUbicacion,
        handleSeleccionarSugerencia,
        handleMapClick,
        usarMiUbicacion,
        calcularFlete
    };

    return (
        <div>
            {/* Header */}
            {showHeader && <LoteHeader lote={lote} />}

            {/* Layout: Video | Data Sidebar | Tabs */}
            <div className="flex flex-col lg:flex-row gap-2 lg:gap-1 mb-2">
                {/* Video Section */}
                <LoteVideo
                    lote={lote}
                    showButtons={showButtons}
                    onPreofertar={handlePreofertar}
                    onShare={handleShare}
                />

                {/* Data Sidebar */}
                <LoteDataSidebar lote={lote} />

                {/* Promedios */}
                <LotePromedios lote={lote} />

                {/* Tabs Section */}
                <div className="w-full lg:w-[45%] bg-white rounded shadow-md flex flex-col">
                    {/* Desktop Tabs */}
                    <div className="hidden lg:flex border-b border-gray-200 px-1">
                        <button
                            onClick={() => setActiveTab('general')}
                            className={`flex-1 py-2 text-xs font-bold transition-all ${activeTab === 'general'
                                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                        >
                            GENERAL
                        </button>
                        <button
                            onClick={() => setActiveTab('ubicacion')}
                            className={`flex-1 py-2 text-xs font-bold transition-all ${activeTab === 'ubicacion'
                                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                        >
                            UBICACIÓN
                        </button>
                        <button
                            onClick={() => setActiveTab('observaciones')}
                            className={`flex-1 py-2 text-xs font-bold transition-all ${activeTab === 'observaciones'
                                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                        >
                            OBSERVACIONES
                        </button>
                    </div>

                    {/* Desktop Tab Content */}
                    <div className="hidden lg:block p-2 h-[500px] overflow-y-auto">
                        {activeTab === 'general' && <LoteTabGeneral lote={lote} />}

                        {activeTab === 'ubicacion' && (
                            <div className="space-y-2">
                                {/* Ubicación info + Freight Calculator */}
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="bg-blue-50 rounded p-2 border border-blue-200">
                                        <p className="text-xs text-blue-700 font-semibold flex items-center gap-1 mb-2">
                                            <MapPin className="w-4 h-4" />
                                            Ubicación del Ganado
                                        </p>
                                        <div className="space-y-1">
                                            <div className="bg-white rounded p-2 border border-blue-200">
                                                <p className="text-[10px] text-blue-600 font-semibold">Establecimiento</p>
                                                <p className="text-sm font-bold text-blue-900">{lote.establecimiento}</p>
                                            </div>
                                            {lote.ubicacion && (
                                                <div className="bg-white rounded p-2 border border-blue-200">
                                                    <p className="text-[10px] text-blue-600 font-semibold">Paraje</p>
                                                    <p className="text-sm font-bold text-blue-900">{lote.ubicacion}</p>
                                                </div>
                                            )}
                                            <div className="bg-white rounded p-2 border border-blue-200">
                                                <p className="text-[10px] text-blue-600 font-semibold">Departamento</p>
                                                <p className="text-sm font-bold text-blue-900">{lote.departamento}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Freight Calculator */}
                                    <div className="bg-green-50 rounded p-2 border border-green-200">
                                        <p className="text-xs text-green-700 font-semibold flex items-center gap-1 mb-2">
                                            <Calculator className="w-4 h-4" />
                                            Calculadora de Flete
                                        </p>
                                        <div className="space-y-2">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={direccionFlete}
                                                    onChange={(e) => {
                                                        setDireccionFlete(e.target.value);
                                                        if (destinoCoords) {
                                                            setDestinoCoords(null);
                                                            setRouteCoords([]);
                                                            setDistanciaKm(null);
                                                            setCostoFlete(null);
                                                        }
                                                    }}
                                                    onFocus={() => sugerencias.length > 0 && setMostrarSugerencias(true)}
                                                    placeholder="Dirección de destino"
                                                    className="w-full border border-green-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                                                />
                                                {buscandoSugerencias && (
                                                    <div className="absolute right-2 top-1.5">
                                                        <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                                                    </div>
                                                )}
                                                {mostrarSugerencias && sugerencias.length > 0 && (
                                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto">
                                                        {sugerencias.map((s, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleSeleccionarSugerencia(s)}
                                                                className="w-full text-left px-2 py-1.5 text-xs hover:bg-green-50 border-b border-gray-100 last:border-b-0"
                                                            >
                                                                {s.display_name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                onClick={usarMiUbicacion}
                                                disabled={obteniendoUbicacion}
                                                className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 py-1.5 rounded text-xs font-semibold flex items-center justify-center gap-1 border border-blue-200 disabled:opacity-50"
                                            >
                                                {obteniendoUbicacion ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
                                                {obteniendoUbicacion ? 'Obteniendo...' : 'Usar mi ubicación'}
                                            </button>
                                            <p className="text-[10px] text-gray-500 italic">O haz clic en el mapa para seleccionar destino</p>
                                            <input
                                                type="number"
                                                value={precioPorKm}
                                                onChange={(e) => setPrecioPorKm(e.target.value)}
                                                placeholder="Precio por km"
                                                className="w-full border border-green-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                                            />
                                            <button
                                                onClick={calcularFlete}
                                                disabled={calculandoFlete}
                                                className="w-full bg-green-600 hover:bg-green-700 text-white py-1.5 rounded text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                                            >
                                                {calculandoFlete ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                                                {calculandoFlete ? 'Calculando...' : 'Calcular Flete'}
                                            </button>
                                            {fleteError && (
                                                <p className="text-xs text-red-600 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" />
                                                    {fleteError}
                                                </p>
                                            )}
                                            {distanciaKm !== null && (
                                                <div className="bg-white rounded p-2 border border-green-300 space-y-1">
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Distancia:</span>
                                                        <span className="font-bold text-gray-900">{distanciaKm.toFixed(1)} km</span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Costo estimado:</span>
                                                        <span className="font-bold text-green-700">${costoFlete.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Map */}
                                <div className="bg-white rounded overflow-hidden border border-gray-200" style={{ height: '220px' }}>
                                    {lote.latitude && lote.longitude ? (
                                        <MapContainer
                                            center={[lote.latitude, lote.longitude]}
                                            zoom={7}
                                            style={{ height: '100%', width: '100%' }}
                                            scrollWheelZoom={false}
                                        >
                                            <TileLayer
                                                attribution='&copy; OpenStreetMap'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            <MapClickHandler onMapClick={handleMapClick} />
                                            <Marker position={[lote.latitude, lote.longitude]}>
                                                <Popup>
                                                    <strong>{lote.establecimiento}</strong><br />
                                                    {lote.ubicacion && <>{lote.ubicacion}<br /></>}
                                                    {lote.departamento}
                                                </Popup>
                                            </Marker>
                                            {destinoCoords && (
                                                <Marker position={[destinoCoords.lat, destinoCoords.lng]} icon={redIcon}>
                                                    <Popup>Destino</Popup>
                                                </Marker>
                                            )}
                                            {routeCoords.length > 0 && (
                                                <Polyline positions={routeCoords} color="green" weight={4} />
                                            )}
                                        </MapContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                                            Ubicación no disponible
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'observaciones' && <LoteTabObservaciones lote={lote} />}
                    </div>

                    {/* Mobile Accordion Sections */}
                    <div className="lg:hidden p-2 space-y-2">
                        {/* GENERAL Section */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => toggleSection('general')}
                                className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-green-100 text-green-800 font-bold text-sm"
                            >
                                <span className="flex items-center gap-2">
                                    <Info className="w-4 h-4" />
                                    GENERAL
                                </span>
                                <ChevronDown className={`w-5 h-5 transition-transform ${expandedSections.general ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedSections.general && (
                                <div className="p-3 bg-white">
                                    <LoteTabGeneral lote={lote} />
                                </div>
                            )}
                        </div>

                        {/* UBICACIÓN Section */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => toggleSection('ubicacion')}
                                className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 font-bold text-sm"
                            >
                                <span className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    UBICACIÓN
                                </span>
                                <ChevronDown className={`w-5 h-5 transition-transform ${expandedSections.ubicacion ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedSections.ubicacion && (
                                <div className="p-3 space-y-2 bg-white">
                                    <div className="bg-blue-50 rounded p-2 border border-blue-200">
                                        <p className="text-xs text-blue-700 font-semibold flex items-center gap-1 mb-2">
                                            <MapPin className="w-4 h-4" />
                                            Ubicación del Ganado
                                        </p>
                                        <div className="space-y-1">
                                            <div className="bg-white rounded p-2 border border-blue-200">
                                                <p className="text-[10px] text-blue-600 font-semibold">Establecimiento</p>
                                                <p className="text-sm font-bold text-blue-900">{lote.establecimiento}</p>
                                            </div>
                                            {lote.ubicacion && (
                                                <div className="bg-white rounded p-2 border border-blue-200">
                                                    <p className="text-[10px] text-blue-600 font-semibold">Paraje</p>
                                                    <p className="text-sm font-bold text-blue-900">{lote.ubicacion}</p>
                                                </div>
                                            )}
                                            <div className="bg-white rounded p-2 border border-blue-200">
                                                <p className="text-[10px] text-blue-600 font-semibold">Departamento</p>
                                                <p className="text-sm font-bold text-blue-900">{lote.departamento}</p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Simplified map for mobile */}
                                    <div className="bg-white rounded overflow-hidden border border-gray-200" style={{ height: '200px' }}>
                                        {lote.latitude && lote.longitude ? (
                                            <MapContainer
                                                center={[lote.latitude, lote.longitude]}
                                                zoom={7}
                                                style={{ height: '100%', width: '100%' }}
                                                scrollWheelZoom={false}
                                            >
                                                <TileLayer
                                                    attribution='&copy; OpenStreetMap'
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                />
                                                <Marker position={[lote.latitude, lote.longitude]}>
                                                    <Popup>
                                                        <strong>{lote.establecimiento}</strong><br />
                                                        {lote.ubicacion && <>{lote.ubicacion}<br /></>}
                                                        {lote.departamento}
                                                    </Popup>
                                                </Marker>
                                            </MapContainer>
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                                                Ubicación no disponible
                                            </div>
                                        )}
                                    </div>
                                    {/* Simple freight calculator for mobile */}
                                    <div className="bg-green-50 rounded p-2 border border-green-200">
                                        <p className="text-xs text-green-700 font-semibold flex items-center gap-1 mb-2">
                                            <Calculator className="w-4 h-4" />
                                            Calculadora de Flete
                                        </p>
                                        <div className="space-y-2">
                                            <input
                                                type="text"
                                                value={direccionFlete}
                                                onChange={(e) => {
                                                    setDireccionFlete(e.target.value);
                                                    if (destinoCoords) {
                                                        setDestinoCoords(null);
                                                        setRouteCoords([]);
                                                        setDistanciaKm(null);
                                                        setCostoFlete(null);
                                                    }
                                                }}
                                                placeholder="Dirección de destino"
                                                className="w-full border border-green-300 rounded px-2 py-1.5 text-xs"
                                            />
                                            <input
                                                type="number"
                                                value={precioPorKm}
                                                onChange={(e) => setPrecioPorKm(e.target.value)}
                                                placeholder="Precio por km"
                                                className="w-full border border-green-300 rounded px-2 py-1.5 text-xs"
                                            />
                                            <button
                                                onClick={calcularFlete}
                                                disabled={calculandoFlete}
                                                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded text-xs font-bold"
                                            >
                                                {calculandoFlete ? 'Calculando...' : 'Calcular Flete'}
                                            </button>
                                            {distanciaKm !== null && (
                                                <div className="bg-white rounded p-2 border border-green-300 space-y-1">
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Distancia:</span>
                                                        <span className="font-bold text-gray-900">{distanciaKm.toFixed(1)} km</span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Costo estimado:</span>
                                                        <span className="font-bold text-green-700">${costoFlete.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* OBSERVACIONES Section */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => toggleSection('observaciones')}
                                className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 font-bold text-sm"
                            >
                                <span className="flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    OBSERVACIONES
                                </span>
                                <ChevronDown className={`w-5 h-5 transition-transform ${expandedSections.observaciones ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedSections.observaciones && (
                                <div className="p-3 bg-white">
                                    <LoteTabObservaciones lote={lote} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onSuccess={() => {
                    setShowLoginModal(false);
                    setShowPreofertaModal(true);
                }}
            />

            <PreofertaModal
                isOpen={showPreofertaModal}
                onClose={() => setShowPreofertaModal(false)}
                lote={lote}
            />
        </div>
    );
};

export default LoteDetalleContent;
